import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { inr, INSTAGRAM_URL } from "@/lib/content";
import { track, getFbp, getFbc } from "@/lib/metaPixel";
import CustomsNotice from "@/components/CustomsNotice";
import { gTrack } from "@/lib/ga";
import { FadeUp } from "@/components/Reveal";

const inputCls =
  "w-full border border-line bg-transparent px-4 py-3.5 font-mono text-base text-bone placeholder:text-bone/30 outline-none transition-colors focus:border-violet sm:text-sm";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

function Success({ order }) {
  const isRazorpay = order.payment_mode === "razorpay";
  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-ink px-6 py-20" data-testid="checkout-success">
      <FadeUp className="max-w-md text-center">
        <span className="mx-auto flex h-14 w-14 rotate-45 items-center justify-center border-2 border-gold" aria-hidden="true">
          <Check className="h-6 w-6 -rotate-45 text-gold" strokeWidth={1.5} />
        </span>
        <h1 className="mt-8 font-display text-5xl tracking-[0.02em] text-bone">
          {isRazorpay ? "ORDER CONFIRMED." : "ORDER RESERVED."}
        </h1>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-gold" data-testid="order-number">
          ORDER {order.number}
        </p>
        <p className="mt-6 text-sm leading-relaxed text-bone/65">
          {isRazorpay
            ? "Payment received. Your pieces are being prepared — a confirmation and tracking link will follow by email."
            : "Your order is saved, but the payment gateway is not connected yet. We will reach out on Instagram to complete payment — or write to us with your order number."}
        </p>
        <div className="mt-9 flex flex-col items-center gap-3">
          <Link to="/" data-testid="success-home-button" className="bg-gold px-10 py-4 font-display text-xl tracking-[0.15em] text-ink transition-colors hover:bg-bone">
            BACK TO HOME
          </Link>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="success-instagram-link"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50 underline decoration-gold/50 underline-offset-4 hover:text-gold"
          >
            QUESTIONS? DM @REQUIEMFEVER
          </a>
        </div>
      </FadeUp>
    </div>
  );
}

export default function Checkout() {
  const { items, subtotal, updateQty, remove, clear, count } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", instagram: "", address: "", city: "", state: "", pincode: "" });
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = "CHECKOUT — REQUIEM FEVER";
  }, []);

  // Meta: InitiateCheckout — once per checkout visit with a non-empty bag.
  const firedInit = useRef(false);
  useEffect(() => {
    if (firedInit.current || !items.length) return;
    firedInit.current = true;
    track("InitiateCheckout", {
      content_type: "product",
      content_ids: items.map((x) => x.slug),
      currency: "INR",
      value: subtotal,
    });
    gTrack("begin_checkout", {
      currency: "INR",
      value: subtotal,
      items: items.map((x) => ({ item_id: x.slug, item_name: x.name, price: x.price, quantity: x.qty })),
    });
  }, [items, subtotal]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.name.trim()) return "PLEASE ENTER YOUR FULL NAME";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "PLEASE ENTER A VALID EMAIL";
    if (form.phone.replace(/\D/g, "").length < 10) return "PLEASE ENTER A VALID 10-DIGIT PHONE";
    if (!form.address.trim()) return "PLEASE ENTER YOUR STREET ADDRESS";
    if (!form.city.trim()) return "PLEASE ENTER YOUR CITY";
    if (!form.state.trim()) return "PLEASE ENTER YOUR STATE";
    if (!/^\d{6}$/.test(form.pincode)) return "PIN CODE MUST BE 6 DIGITS";
    return null;
  };

  const pay = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    if (!items.length) return toast.error("YOUR BAG IS EMPTY");
    setPlacing(true);
    try {
      const payload = {
        items: items.map((x) => ({ slug: x.slug, size: x.size || "", variant: x.variant || "", qty: x.qty })),
        customer: form,
      };
      const { data } = await api.post("/checkout/create", payload);
      if (data.razorpay) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not load the payment gateway. Check your connection and retry.");
        const rzp = new window.Razorpay({
          key: data.razorpay.key,
          amount: data.razorpay.amount,
          currency: data.razorpay.currency,
          name: "REQUIEM FEVER",
          description: `Order ${data.order.number}`,
          order_id: data.razorpay.order_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#7944C8" },
          handler: async (res) => {
            try {
              const v = await api.post("/checkout/verify", {
                order_id: data.order.id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
                fbp: getFbp(),
                fbc: getFbc(),
              });
              clear();
              // Meta: browser-side Purchase — deduped with the server CAPI event via event_id.
              track(
                "Purchase",
                {
                  currency: "INR",
                  value: v.data.order.total,
                  content_type: "product",
                  content_ids: v.data.order.items.map((it) => it.slug),
                },
                v.data.event_id,
              );
              gTrack("purchase", {
                transaction_id: v.data.order.number,
                currency: "INR",
                value: v.data.order.total,
                items: v.data.order.items.map((it) => ({
                  item_id: it.slug,
                  item_name: it.name,
                  price: it.price,
                  quantity: it.qty,
                })),
              });
              setDone(v.data.order);
            } catch (e2) {
              toast.error(e2.response?.data?.detail || "PAYMENT VERIFICATION FAILED");
            }
            setPlacing(false);
          },
          modal: {
            ondismiss: () => {
              setPlacing(false);
              toast("PAYMENT CANCELLED — YOUR BAG IS SAFE");
            },
          },
        });
        rzp.open();
      } else {
        clear();
        setDone(data.order);
        setPlacing(false);
      }
    } catch (e3) {
      toast.error(e3.response?.data?.detail || e3.message || "SOMETHING WENT WRONG");
      setPlacing(false);
    }
  };

  if (done) return <Success order={done} />;

  if (!items.length) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 bg-ink px-6 text-center" data-testid="checkout-empty">
        <ShoppingBag className="h-8 w-8 text-bone/30" strokeWidth={1.25} />
        <p className="font-display text-4xl tracking-wide text-bone">YOUR BAG IS EMPTY</p>
        <Link to="/shop" data-testid="checkout-empty-shop-link" className="border border-gold px-8 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink">
          SHOP THE DROP
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-ink" data-testid="checkout-page">
      <CustomsNotice />
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
        <FadeUp>
          <h1 className="font-display text-5xl leading-[0.9] tracking-[0.01em] text-bone sm:text-6xl">CHECKOUT</h1>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/50">
            SHIPPING ACROSS INDIA · FREE ON EVERY ORDER
          </p>
        </FadeUp>

        <form onSubmit={pay} className="mt-12 grid gap-12 lg:grid-cols-12" data-testid="checkout-form">
          <div className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">01 — SHIPPING DETAILS</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input data-testid="checkout-name-input" aria-label="Full name" placeholder="FULL NAME" value={form.name} onChange={set("name")} className={`${inputCls} sm:col-span-2`} />
              <input data-testid="checkout-email-input" aria-label="Email" type="email" placeholder="EMAIL" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="checkout-phone-input" aria-label="Phone" type="tel" placeholder="PHONE (10 DIGITS)" value={form.phone} onChange={set("phone")} className={inputCls} />
              <div className="sm:col-span-2">
                <input data-testid="checkout-instagram-input" aria-label="Instagram handle (optional)" placeholder="INSTAGRAM HANDLE (OPTIONAL)" value={form.instagram} onChange={set("instagram")} className={`${inputCls} w-full`} />
                <p className="mt-2 font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-bone/40" data-testid="checkout-instagram-hint">
                  ADDING YOUR HANDLE HELPS US REACH YOU DIRECTLY ON INSTAGRAM IN CASE OF ANY ISSUES WITH YOUR ORDER
                </p>
              </div>
              <input data-testid="checkout-address-input" aria-label="Street address" placeholder="STREET ADDRESS" value={form.address} onChange={set("address")} className={`${inputCls} sm:col-span-2`} />
              <input data-testid="checkout-city-input" aria-label="City" placeholder="CITY" value={form.city} onChange={set("city")} className={inputCls} />
              <input data-testid="checkout-state-input" aria-label="State" placeholder="STATE" value={form.state} onChange={set("state")} className={inputCls} />
              <input data-testid="checkout-pincode-input" aria-label="Pin code" placeholder="PIN CODE (6 DIGITS)" value={form.pincode} onChange={set("pincode")} className={`${inputCls} sm:col-span-2`} maxLength={6} />
            </div>
          </div>

          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">02 — YOUR ORDER</p>
            <div className="mt-6 border border-line" data-testid="order-summary">
              <div className="max-h-72 overflow-y-auto">
                {items.map((it) => {
                  const suffix = [it.variant, it.size].filter(Boolean).join("-");
                  return (
                    <div key={`${it.slug}-${suffix}`} data-testid={`summary-item-${it.slug}-${suffix}`} className="flex gap-4 border-b border-line/60 p-4">
                      <img src={it.image || "/logo.svg"} alt={it.name} className="h-20 w-16 shrink-0 object-cover" />
                      <div className="flex flex-1 flex-col justify-between py-0.5">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-display text-base leading-tight tracking-wide text-bone">{it.name}</p>
                          <p className="font-mono text-xs text-bone">{inr(it.price * it.qty)}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50">
                            {[it.variant, it.size && `SIZE ${it.size}`].filter(Boolean).join(" · ")} · QTY{" "}
                            <button type="button" data-testid={`summary-minus-${it.slug}-${suffix}`} onClick={() => (it.qty === 1 ? remove(it.slug, it.size, it.variant) : updateQty(it.slug, it.size, it.variant, it.qty - 1))} className="px-1 text-bone/60 hover:text-gold">−</button>
                            <span data-testid={`summary-qty-${it.slug}-${suffix}`}>{it.qty}</span>
                            <button type="button" data-testid={`summary-plus-${it.slug}-${suffix}`} onClick={() => updateQty(it.slug, it.size, it.variant, it.qty + 1)} className="px-1 text-bone/60 hover:text-gold">+</button>
                          </p>
                          <button type="button" data-testid={`summary-remove-${it.slug}-${suffix}`} onClick={() => remove(it.slug, it.size, it.variant)} className="font-mono text-[9px] uppercase tracking-[0.2em] text-bone/40 hover:text-magenta">
                            REMOVE
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-2 p-5">
                <div className="flex justify-between font-mono text-xs uppercase tracking-[0.2em] text-bone/60">
                  <span>SUBTOTAL</span>
                  <span className="text-bone" data-testid="checkout-subtotal">{inr(subtotal)}</span>
                </div>
                <div className="flex justify-between font-mono text-xs uppercase tracking-[0.2em] text-bone/60">
                  <span>SHIPPING</span>
                  <span className="text-gold">FREE</span>
                </div>
                <div className="flex justify-between border-t border-line pt-3 font-mono text-sm uppercase tracking-[0.2em] text-bone">
                  <span>TOTAL</span>
                  <span data-testid="checkout-total">{inr(subtotal)}</span>
                </div>
                <p className="pt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-bone/40">{count} ITEM(S) · INCL. ALL TAXES</p>
              </div>
            </div>
            <button
              data-testid="checkout-pay-button"
              type="submit"
              disabled={placing}
              className="mt-6 w-full bg-violet py-4 font-display text-xl tracking-[0.15em] text-bone transition-colors duration-300 hover:bg-magenta disabled:opacity-50"
            >
              {placing ? "PROCESSING…" : `PROCEED TO PAY ${inr(subtotal)}`}
            </button>
            <p className="mt-4 text-center font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-bone/40">
              SECURE PAYMENTS VIA RAZORPAY · UPI, CARDS, NETBANKING & WALLETS
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
